import cloneDeep from 'lodash/cloneDeep';
import filter from 'lodash/filter';
import findIndex from 'lodash/findIndex';
import forEach from 'lodash/forEach';
import get from 'lodash/get';
import groupBy from 'lodash/groupBy';

import createCollectionResource, { OptionsType } from 'src/actions/shared/createResource';
import { DomainError as StandardDomainError, Unauthorized } from '@ad-scripts/application/lib/error';
import Ivr from 'src/models/Ivrs/Ivr';
import ivrResource from './ivrResource';
import { logout } from 'src/actions/auth/authSlice';
import { toastError, toastSuccess } from 'src/actions/toast';
import Thread from 'src/models/Thread/Thread';
import { translateIvrPublishServerErrors } from './helpers/translateIvrPublishServerErrors';

const baseIvrsResource = <T>({
  name,
  endpoint,
  type,
  options,
}: {
  name: string;
  endpoint: string | Record<string, string>;
  options?: OptionsType;
  type: { new (): T };
}) => {
  const resource = createCollectionResource<Ivr>({
    name,
    endpoint,
    options: { complexKey: ['id', 'version'], ...(options ? options : {}) },
    type,
    reducers: {
      addSuccess(state, action) {
        state.persistingState = 'succeeded';
        state.values.push(action.payload);
      },
      publishStarted(state) {
        state.persistingState = 'persisting';
      },
      publishError(state, action) {
        state.persistingState = 'failed';
        state.errors = action.payload;
      },
      publishSuccess(state, action) {
        state.persistingState = 'succeeded';
        const publishedIndex = findIndex(
          state.values,
          (item: any) => item.id === action.payload.id && item.version === action.payload.version
        );
        state.values[publishedIndex] = {
          ...state.values[publishedIndex],
          publishedAt: action.payload.publishedAt,
          comment: action.payload.comment,
        };
        // remove all draft versions
        state.values = filter(state.values, value => value.publishedAt || value.id !== action.payload.id);
      },
      restoreVersionStarted(state) {
        state.persistingState = 'persisting';
      },
      restoreVersionError(state, action) {
        state.persistingState = 'failed';
        state.errors = action.payload;
      },
      restoreVersionSuccess(state, action) {
        state.persistingState = 'succeeded';
        const currentIndex = findIndex(
          state.values,
          (value: any) => value.id === action.payload.id && value.version === action.payload.version
        );
        if (currentIndex !== -1) {
          state.values[currentIndex] = { ...action.payload };
        } else {
          state.values.push(action.payload);
        }
        // remove all draft versions exclude current one
        state.values = filter(
          state.values,
          value =>
            value.id !== action.payload.id ||
            (value.id === action.payload.id && value.version <= action.payload.version)
        );
      },
      renameSuccess(state, action) {
        state.persistingState = 'succeeded';
        const ivrId = get(action, 'payload.id');
        const ivrName = get(action, 'payload.name');
        forEach(state.values, value => {
          if (value.id === ivrId) {
            value.name = ivrName;
          }
        });
      },
      archiveSuccess(state, action) {
        state.persistingState = 'succeeded';
        const ivrId = get(action, 'payload.id');
        const ivrArchivedAt = get(action, 'payload.archivedAt');
        forEach(state.values, value => {
          if (value.id === ivrId) {
            value.archivedAt = ivrArchivedAt;
          }
        });
      },
    },
    observerCreator: ({ actions }) => {
      return ({ dispatch }) => {
        return {
          onBroadcast(item) {
            dispatch(actions.broadcast(item));
          },
        };
      };
    },
  });

  resource.effects.promote = promote(resource);

  return resource;
};

const promote =
  resource =>
  <T extends Ivr | Thread>(params: T) =>
  async (dispatch, getState, { gateways }) => {
    try {
      dispatch(resource.actions.publishStarted());
      const result = await gateways[resource.name].promote(
        { id: params.id, version: params.version },
        { comment: params.comment }
      );
      const state = getState();

      dispatch(
        ivrResource.actions.fetchSuccess({
          ...cloneDeep(state[resource.name].value),
          publishedAt: new Date().toISOString(),
          comment: result.comment,
        })
      );

      dispatch(resource.actions.publishSuccess(result));
      dispatch(toastSuccess('Publish successful!'));

      return true;
    } catch (err) {
      if (err instanceof StandardDomainError) {
        const translatedErrors = translateIvrPublishServerErrors(err.errors);
        dispatch(resource.actions.publishError(translatedErrors));
        const errors = Object.keys(groupBy(translatedErrors, 'detail')).join(', ');
        dispatch(toastError(`Errors: ${errors}`));
        return false;
      } else if (err instanceof Unauthorized) {
        dispatch(logout());
      }
      dispatch(toastError(`There was an error while publishing`));
      return false;
    }
  };

export default baseIvrsResource;
